import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Upload, Building2 } from "lucide-react";

const CURRENCIES = [
  { v: "BRL", l: "Real (BRL · R$)" },
  { v: "USD", l: "Dólar (USD · $)" },
  { v: "EUR", l: "Euro (EUR · €)" },
  { v: "GBP", l: "Libra (GBP · £)" },
];
const LOCALES = [
  { v: "pt-BR", l: "Português (Brasil)" },
  { v: "pt-PT", l: "Português (Portugal)" },
  { v: "en-US", l: "English (US)" },
  { v: "es-ES", l: "Español (España)" },
];
const DATE_FORMATS = [
  { v: "DD/MM/YYYY", l: "DD/MM/YYYY" },
  { v: "MM/DD/YYYY", l: "MM/DD/YYYY" },
  { v: "YYYY-MM-DD", l: "YYYY-MM-DD" },
];

function MembersSection() {
  const { currentOrg } = useAuth();
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (currentOrg?.role === "owner") {
      api.get(`/organizations/${currentOrg.id}/members`).then((r) => setMembers(r.data)).catch(() => {});
    }
  }, [currentOrg?.id, currentOrg?.role]);
  if (currentOrg?.role !== "owner") return null;
  const setRole = async (uid, role) => {
    try {
      await api.patch(`/organizations/${currentOrg.id}/members/${uid}`, { role });
      setMembers((m) => m.map((x) => (x.user_id === uid ? { ...x, role } : x)));
      toast.success("Perfil atualizado");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="members-section">
      <h2 className="font-display text-lg font-semibold">Perfis de Acesso</h2>
      <p className="text-muted-foreground text-sm mt-1 mb-4">Separe operador de vendas de gestor financeiro. Somente gestores/proprietário veem o resultado financeiro.</p>
      <div className="space-y-3">
        {members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between gap-3 border-b border-border pb-3 last:border-0" data-testid={`member-${m.email}`}>
            <div className="min-w-0"><p className="font-medium truncate">{m.name}</p><p className="text-xs text-muted-foreground truncate">{m.email}</p></div>
            <Select value={m.role} onValueChange={(v) => setRole(m.user_id, v)}>
              <SelectTrigger className="w-[200px]" data-testid={`member-role-${m.email}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Proprietário</SelectItem>
                <SelectItem value="finance_admin">Gestor financeiro</SelectItem>
                <SelectItem value="finance_viewer">Visualizar financeiro</SelectItem>
                <SelectItem value="sales_operator">Operador de vendas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoSection() {
  const { currentOrg, refreshOrgs } = useAuth();
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Arquivo muito grande (máximo 2MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/organizations/${currentOrg.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo atualizada");
      await refreshOrgs();
      window.location.reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/organizations/${currentOrg.id}/logo`);
      toast.success("Logo removida");
      await refreshOrgs();
      window.location.reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm mt-6" data-testid="logo-section">
      <h2 className="font-display text-lg font-semibold">Logo da empresa</h2>
      <p className="text-muted-foreground text-sm mt-1 mb-4">Exibida no topo esquerdo em todas as páginas. PNG, JPG, WEBP ou SVG, até 2MB.</p>
      <div className="flex items-center gap-5">
        <div className="h-16 w-16 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
          {currentOrg?.has_logo
            ? <img src={`${BACKEND}/api/organizations/${currentOrg.id}/logo?v=${currentOrg.logo_updated_at || ""}`} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
            : <Building2 className="h-6 w-6 text-muted-foreground" />}
        </div>
        <div className="flex gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onFile} data-testid="logo-file-input" />
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="logo-upload-button">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {currentOrg?.has_logo ? "Trocar logo" : "Enviar logo"}
          </Button>
          {currentOrg?.has_logo && <Button variant="ghost" onClick={remove} disabled={uploading} data-testid="logo-remove-button">Remover</Button>}
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { currentOrg, refreshOrgs } = useAuth();
  const [f, setF] = useState({ name: "", currency: "BRL", locale: "pt-BR", date_format: "DD/MM/YYYY" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentOrg) {
      setF({
        name: currentOrg.name || "",
        currency: currentOrg.currency || "BRL",
        locale: currentOrg.locale || "pt-BR",
        date_format: currentOrg.date_format || "DD/MM/YYYY",
      });
    }
  }, [currentOrg]);

  const preview = () => {
    try {
      const money = new Intl.NumberFormat(f.locale, { style: "currency", currency: f.currency }).format(1234.5);
      const d = new Date(2026, 2, 9);
      const date = f.date_format.replace("DD", "09").replace("MM", "03").replace("YYYY", "2026");
      return `${money} · ${date}`;
    } catch {
      return "—";
    }
  };

  const save = async () => {
    if (!currentOrg) return;
    setSaving(true);
    try {
      await api.patch(`/organizations/${currentOrg.id}`, f);
      await refreshOrgs();
      toast.success("Configurações salvas");
      window.location.reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Configurações da Organização</h1>
        <p className="text-muted-foreground mt-1">Moeda, idioma e formato de data aplicados em todo o sistema</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm space-y-5">
        <div className="space-y-2">
          <Label>Nome da organização</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-name-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Moeda</Label>
            <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
              <SelectTrigger data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Idioma / Região</Label>
            <Select value={f.locale} onValueChange={(v) => setF({ ...f, locale: v })}>
              <SelectTrigger data-testid="settings-locale-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCALES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Formato de data</Label>
            <Select value={f.date_format} onValueChange={(v) => setF({ ...f, date_format: v })}>
              <SelectTrigger data-testid="settings-dateformat-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_FORMATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-lg bg-secondary px-4 py-3">
          <p className="section-label">Pré-visualização</p>
          <p className="font-num text-lg font-semibold mt-1" data-testid="settings-preview">{preview()}</p>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="settings-save-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar configurações
          </Button>
        </div>
      </div>

      <LogoSection />
      <MembersSection />
    </div>
  );
}
